import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Wallet } from 'lucide-react';
import { api } from '../api/cliente';
import type { Orcamento } from '../api/tipos';
import { numero, plural, reais } from '../util/formato';
import { Botao, Cartao, Esqueleto, cx } from './ui';
import { ModalRecarga } from './ModalRecarga';
import css from './ResumoCusto.module.css';

/**
 * Quanto vai custar e se o saldo cobre.
 *
 * Usado em TODOS os pontos que iniciam uma busca — chaves coladas, CSV e SPED —
 * para o preço aparecer sempre da mesma forma. Antes, cada tela mostrava (ou
 * deixava de mostrar) o custo do seu jeito, e a única estimativa visível era um
 * "~XMLs" calculado no front com preço fixo, que errava fora da faixa.
 *
 * O cálculo vem do servidor: é ele que conhece a tabela de faixas, e é a mesma
 * conta que vai valer na hora de reservar o saldo.
 */
export function ResumoCusto({
  chaves,
  /** Texto quando ainda não há nada selecionado. */
  vazio,
  /**
   * Recebe o orçamento calculado. Existe para a tela poder confirmar o valor
   * na hora de enviar sem repetir a consulta — o número que o usuário viu tem
   * que ser exatamente o que aparece na confirmação.
   */
  aoOrcar,
}: {
  chaves: number;
  vazio?: string;
  aoOrcar?: (o: Orcamento | null) => void;
}) {
  const [orcamento, setOrcamento] = useState<Orcamento | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [recarregando, setRecarregando] = useState(false);
  /** Sobe a cada Pix confirmado, para o orçamento ser reconsultado. */
  const [saldoVersao, setSaldoVersao] = useState(0);

  // `useRef` para o callback não entrar nas dependências: uma função recriada a
  // cada render da página faria este efeito refazer a consulta sem parar.
  const notificar = useRef(aoOrcar);
  notificar.current = aoOrcar;

  useEffect(() => {
    if (chaves <= 0) {
      setOrcamento(null);
      notificar.current?.(null);
      return;
    }
    let vivo = true;
    setCarregando(true);
    api<Orcamento>(`/creditos/orcamento?chaves=${chaves}`)
      .then((o) => {
        if (!vivo) return;
        setOrcamento(o);
        notificar.current?.(o);
      })
      .catch(() => undefined)
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [chaves, saldoVersao]);

  if (chaves <= 0) {
    return vazio ? <p className={css.nota}>{vazio}</p> : null;
  }

  const falta = orcamento && !orcamento.suficiente;

  return (
    <div className={css.resumo}>
      <dl className={css.linhas}>
        <div className={css.linha}>
          <dt>Chaves</dt>
          <dd className="num">{numero(chaves)}</dd>
        </div>
        <div className={css.linha}>
          <dt>Preço por XML</dt>
          <dd className="num">
            {orcamento ? reais(orcamento.precoUnitarioCentavos) : <Esqueleto altura={14} largura={56} />}
          </dd>
        </div>
        <div className={cx(css.linha, css.total)}>
          <dt>Total a reservar</dt>
          <dd className="num">
            {orcamento ? reais(orcamento.totalCentavos) : <Esqueleto altura={18} largura={80} />}
          </dd>
        </div>
        <div className={css.linha}>
          <dt>Saldo disponível</dt>
          <dd className="num">
            {orcamento ? reais(orcamento.saldoCentavos) : <Esqueleto altura={14} largura={64} />}
          </dd>
        </div>
      </dl>

      {carregando && !orcamento && <p className={css.nota}>Calculando o custo…</p>}

      {orcamento && (
        <div className={cx(css.situacao, falta ? css.falta : css.cobre)} role="status">
          {falta ? (
            <AlertTriangle className={css.situacaoIcone} aria-hidden="true" />
          ) : (
            <CheckCircle2 className={css.situacaoIcone} aria-hidden="true" />
          )}
          <span>
            {falta
              ? `Faltam ${reais(orcamento.faltamCentavos)} para ${plural(chaves, 'chave')}.`
              : 'O saldo cobre esta busca.'}
          </span>
        </div>
      )}

      {falta && (
        <Botao
          aparencia="primario"
          larguraTotal
          icone={<Wallet />}
          onClick={() => setRecarregando(true)}
        >
          Adicionar crédito
        </Botao>
      )}

      {/* Recarrega sem sair da tela: mandar para a página de Créditos custaria
          as chaves já coladas e o arquivo já escolhido. */}
      <ModalRecarga
        aberto={recarregando}
        aoFechar={() => setRecarregando(false)}
        aoConfirmar={() => setSaldoVersao((v) => v + 1)}
        faltamCentavos={falta ? orcamento.faltamCentavos : undefined}
      />
    </div>
  );
}

/**
 * Como a cobrança funciona.
 *
 * Fica onde o usuário ainda NÃO sabe quanto vai gastar — a tela de envio de
 * SPED, a de nova busca antes de colar as chaves. A dúvida que aparece nesse
 * ponto é sempre a mesma: "vou ser cobrado por mandar o arquivo?" e "se a nota
 * não for encontrada, perco o dinheiro?". Responder antes evita que a resposta
 * venha na forma de um saldo menor sem explicação.
 */
export function ComoCobramos({
  /** Texto de abertura, para adaptar à tela. */
  contexto = 'Analisar os arquivos é gratuito.',
}: {
  contexto?: string;
}) {
  return (
    <Cartao titulo="Como a cobrança funciona">
      <ul className={css.lista}>
        <li>
          <strong className={css.positivo}>{contexto}</strong> Você só paga pelos XMLs, e vê a
          quantidade e o valor antes de confirmar.
        </li>
        <li>
          Ao iniciar o download, o valor total fica <strong>reservado</strong> — sai do saldo
          disponível e não pode ser gasto em outra busca enquanto o lote roda.
        </li>
        <li>
          No fim, <strong>só é cobrado o XML que foi realmente entregue</strong>. Nota não
          encontrada, cancelada ou que falhou não é cobrada:{' '}
          <strong className={css.positivo}>a diferença volta para o seu saldo</strong> e pode ser
          usada na busca seguinte.
        </li>
      </ul>
    </Cartao>
  );
}

/**
 * Custo de um lote cujo tamanho ainda não é conhecido — o caso do CSV grande,
 * que não vale a pena ler no navegador só para contar.
 */
export function CustoIndefinido({ saldoCentavos }: { saldoCentavos?: number }) {
  return (
    <p className={css.nota}>
      O custo é calculado ao enviar, pela quantidade de chaves do arquivo.
      {saldoCentavos !== undefined && ` Saldo disponível: ${reais(saldoCentavos)}.`}
    </p>
  );
}
